import axios, { type AxiosInstance, type AxiosResponse } from "axios";

// Create a custom axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
	baseURL: process.env.NEXT_PUBLIC_API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Set the auth token on the axios instance (called once from SessionLoader)
export const setAxiosToken = (token: string | null) => {
	if (token) {
		axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		delete axiosInstance.defaults.headers.common.Authorization;
	}
};

let isLoggingOut = false;

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error) => {
		const originalRequest = error.config;

		// Handle 401 Unauthorized
		// Skip auto-logout for password change endpoint (wrong password should not log user out)
		if (
			error.response?.status === 401 &&
			!originalRequest._retry &&
			!originalRequest.url?.includes("/auth/change-password") &&
			!originalRequest.url?.includes("/auth/logout")
		) {
			originalRequest._retry = true;

			if (typeof window !== "undefined" && !isLoggingOut) {
				isLoggingOut = true;

				try {
					const { signOut } = await import("next-auth/react");
					await signOut({ redirect: false });
				} catch (signOutErr) {
					console.error("[axios] signOut failed:", signOutErr);
				}

				// Best-effort: clear any non-httpOnly cookies on this host
				try {
					for (const c of document.cookie.split(";")) {
						const name = c.split("=")[0].trim();
						// biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks broad support; best-effort clear on logout
						document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
					}
				} catch {
					// ignore
				}

				/*
					Straight to the login. A 401 *is* the expiry - there is nothing
					to tell the user that the login screen does not already say, and
					the interstitial that used to sit here could be reached with no
					session ever having existed, which read as "your session expired"
					to somebody who had never signed in.
				*/
				window.location.replace("/auth/login");
			}
		}

		return Promise.reject(error);
	},
);

export default axiosInstance;
